// Service promises: one table (admin: Website → Navbar, footer & offices → "Service promises",
// site-wide content `promises`, defaults in src/data/globalDefaults.js), shown in the page text
// through tokens:
//   {dispatch}      phones answered 24/7
//   {quoteReply}    within 24 hours
//   {urgent}        urgent staffing: Montréal → urgentMontreal, any other city → urgentOther,
//                   a page with no city (home, services, about, …) → urgentAll
//   {urgentShort}   the same, short (stats): urgentShortMontreal / urgentShortOther / urgentShortAll
//   {plannedStart}  typically 24–48 hours after signing
//   {alarm}         24/7 alarm response by mobile patrol
// resolvePromises() replaces them in every string of a value (objects and arrays, deep copy),
// before the page renders and before its JSON-LD / meta tags are built. Unknown tokens
// ({year}, {city}, …) are left as they are. A token that starts a sentence gets a capital
// letter ("{dispatch}." → "Phones answered 24/7."). Pure: works in the prerender.
import SITE_PAGES from "../data/generated/sitePages.json";
import GLOBAL_DEFAULTS from "../data/globalDefaults";
import localizeServicePage from "./localizeServicePage";
import fill from "./fillDefaults";

const TOKEN_RE = /\{(dispatch|quoteReply|urgent|urgentShort|plannedStart|alarm)\}/g;

const isFr = (lang) => lang === true || lang === "fr";

/** The promise table in one language: `g` = resolved site-wide content (its .promises). */
export function promisesFor(g, lang) {
  const defaults = GLOBAL_DEFAULTS[isFr(lang) ? "fr" : "en"].promises;
  return fill(g?.promises, defaults);
}

// Published site-wide promises (the public site), per language
const published = {};
function publishedPromises(lang) {
  const key = isFr(lang) ? "fr" : "en";
  if (!published[key]) {
    const page = SITE_PAGES.global;
    const content = page ? localizeServicePage(page, key === "fr").content : null;
    published[key] = promisesFor(content, key);
  }
  return published[key];
}

function tokenValue(name, p, city) {
  if (name === "urgent" || name === "urgentShort") {
    const suffix = !city ? "All" : city === "montreal" ? "Montreal" : "Other";
    return p[name + suffix];
  }
  return p[name];
}

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function replaceIn(str, p, city) {
  if (!str.includes("{")) return str;
  return str.replace(TOKEN_RE, (match, name, offset, whole) => {
    const v = tokenValue(name, p, city);
    if (typeof v !== "string" || !v) return match;
    const before = whole.slice(0, offset);
    return !before.trim() || /[.!?]\s+$/.test(before) || /\n\s*$/.test(before) ? capitalize(v) : v;
  });
}

/**
 * Replace the promise tokens in `value` (string, object or array; returns a deep copy).
 * opts.city: the page's city key ("montreal", "toronto", …) or none. opts.lang: "en" / "fr"
 * (or true for French). opts.promises: the table to use (default: the published site-wide one).
 */
export function resolvePromises(value, { city, lang, promises } = {}) {
  const p = promises || publishedPromises(lang);
  const walk = (v) => {
    if (typeof v === "string") return replaceIn(v, p, city);
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === "object" && !v.$$typeof && Object.getPrototypeOf(v) === Object.prototype) {
      const out = {};
      for (const k of Object.keys(v)) out[k] = walk(v[k]);
      return out;
    }
    return v; // React elements, dates, functions… stay as they are
  };
  return walk(value);
}

/** True when `value` contains a promise token somewhere (strings, objects, arrays). */
export function hasPromiseToken(value) {
  if (typeof value === "string") return value.search(TOKEN_RE) !== -1;
  if (Array.isArray(value)) return value.some(hasPromiseToken);
  if (value && typeof value === "object") return Object.values(value).some(hasPromiseToken);
  return false;
}
